import { client } from "./supabase";
import { exigirFechaValida, exigirFechaValidaEnLista } from "./validarFechaDocumento";

// ─── PLAN PAGOS COMPROMISO (cuotas de un compromiso de pago) ──────────────────
//
// Espejo EXACTO de src/utils/planPagosActivos.js, colgado de compromisos_pago(id)
// vía plan_pagos_compromiso.compromiso_id en vez de activos. Doctrina heredada
// (ver docs/dominios/compromisos-pago.md): pagar una cuota es CAJA PURA
// (movimiento_bancario_id + fecha_pago_real + estado='pagada'); no ejecuta ni
// deprecia nada (el compromiso ni siquiera deprecia — es un gasto directo). El
// "quién pagó qué" vive en movimiento_bancario_id (cuota→banco). Todo por ID (10.7),
// filtro de país (10.10/10.53), errores se propagan, nunca se tragan a [] (10.170/10.177).

export const getPlanPagosCompromiso = async (compromisoId) => {
  const { data, error } = await client
    .from("plan_pagos_compromiso")
    .select("*")
    .eq("compromiso_id", compromisoId)
    .order("numero_cuota");
  if (error) throw error;
  return data || [];
};

// Set de compromiso_id que tienen AL MENOS una cuota en el plan (cualquier estado). Necesario
// aparte de "tiene cuotas pendientes": si el plan está completo (todas pagadas), el compromiso
// sigue teniendo plan y NO debe volver a proyectar la línea única. Lo usará la bifurcación de
// la proyección de compromisos en FlujoCajaV2 (Fase 3). Espejo de getActivosConPlan.
export const getCompromisosConPlan = async (pais) => {
  const { data, error } = await client.from("plan_pagos_compromiso").select("compromiso_id").eq("pais", pais);
  if (error) throw error;
  return new Set((data || []).map((r) => r.compromiso_id));
};

// [NO PORTADO — comentado a propósito, no borrado a ciegas]
// El molde tiene getPlanCountsByActivo(activoIds) → { activo_id: count }, que alimenta el
// indicador "plan de cuotas (N)" del modal Inversión de Edificios. El compromiso NO tiene un
// modal equivalente (su alta vive en la ficha del proveedor, Fase 5) ni consumidor de ese
// conteo hoy. Se deja el hueco marcado; si aparece el indicador, portarlo es mecánico:
//
// export const getPlanCountsByCompromiso = async (compromisoIds) => {
//   if (!compromisoIds || compromisoIds.length === 0) return {};
//   const { data, error } = await client
//     .from("plan_pagos_compromiso").select("compromiso_id").in("compromiso_id", compromisoIds);
//   if (error) throw error;
//   const counts = {};
//   for (const row of data || []) counts[row.compromiso_id] = (counts[row.compromiso_id] || 0) + 1;
//   return counts;
// };

// Alta de cuotas. El molde es crearPlanPagosActivos(cuotas) (cada cuota ya trae activo_id);
// acá la firma pedida es (compromisoId, cuotas) → se inyecta compromiso_id en cada fila para
// que el caller no lo repita. Mismo comportamiento: insert masivo + devuelve las filas creadas.
// F1 (B.2b, 2026-09-26): `fecha_vencimiento` exige `modo` explícito — mismo candado que
// planCobros.js/planPagosActivos.js; el único caller real (CompromisoPagoModal) siempre pasa
// MODOS_VALIDACION.DATO_DOCUMENTO (plan de cuotas = dato pactado, nunca un MOVER puntual).
export const crearPlanPagosCompromiso = async (compromisoId, cuotas, opciones = {}) => {
  exigirFechaValidaEnLista(cuotas, "fecha_vencimiento", opciones);
  const filas = (cuotas || []).map((c) => ({ ...c, compromiso_id: compromisoId }));
  const { data, error } = await client.from("plan_pagos_compromiso").insert(filas).select();
  if (error) throw error;
  return data || [];
};

export const actualizarCuotaCompromiso = async (id, cambios, opciones = {}) => {
  exigirFechaValida(cambios, "fecha_vencimiento", opciones);
  const { data, error } = await client.from("plan_pagos_compromiso").update(cambios).eq("id", id).select().single();
  if (error) throw error;
  return data;
};

// Borra UNA cuota por id, SOLO si está pendiente (el `.eq("estado","pendiente")` es el candado en
// DB). Nunca toca cuotas pagadas — están vinculadas a un movimiento histórico vía movimiento_bancario_id.
// Si la cuota está pagada (o no existe), no borra nada y lanza error. (Espejo de eliminarCuotaActivo.)
export const eliminarCuotaCompromiso = async (id) => {
  const { data, error } = await client
    .from("plan_pagos_compromiso")
    .delete()
    .eq("id", id)
    .eq("estado", "pendiente")
    .select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error(`No se pudo borrar la cuota ${id} (¿ya está pagada?)`);
  return data[0];
};

// Reversión de cuota: la devuelve a 'pendiente' limpiando el vínculo bancario. Se usará al
// desvincular una cuota de compromiso del banco (fase de pago). Espejo de revertirCuotaActivo.
export const revertirCuotaCompromiso = async (id) => {
  const { data, error } = await client
    .from("plan_pagos_compromiso")
    .update({ estado: "pendiente", fecha_pago_real: null, movimiento_bancario_id: null })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// ─── Cuotas futuras pendientes (proyección de compromisos) ────────────────────
//
// Lee plan_pagos_compromiso + join al compromiso (FK real). Filtra estado='pendiente' + país;
// SIN filtro de fecha → incluye vencidas (patrón 10.155). El join !inner + eliminado=false
// descarta cuotas de compromisos soft-deleteados (el soft-delete NO dispara el ON DELETE
// CASCADE — eso es solo para delete físico). A diferencia de activos, el compromiso NO tiene
// destino_* polimórfico: la etiqueta la arma el consumidor con proveedor + concepto. Espejo de
// getCuotasActivosFuturas.
// `compromisoId` opcional: acota la carga a las cuotas de UN compromiso (refetch dirigido tras el
// alta con plan / editar su plan). Sin el filtro, comportamiento idéntico al de siempre.
export const getCuotasCompromisoFuturas = async (pais, { compromisoId } = {}) => {
  let query = client
    .from("plan_pagos_compromiso")
    .select(
      `
      *,
      compromiso:compromisos_pago!inner(
        id, pais, moneda, monto, concepto, estado, eliminado,
        proveedor:proveedores(id, nombre)
      )
    `
    )
    .eq("estado", "pendiente")
    .eq("pais", pais)
    .eq("compromiso.eliminado", false);
  if (compromisoId != null) query = query.eq("compromiso_id", compromisoId);
  const { data, error } = await query.order("fecha_vencimiento");
  if (error) throw error;
  const cuotas = data || [];
  if (cuotas.length === 0) return cuotas;

  // Enriquecer con `total_cuotas` = total del PLAN (todas las cuotas, cualquier estado) para el
  // label "cuota N/M". El select de arriba filtra estado='pendiente', así que su conteo daría solo
  // las pendientes (M mal); se cuenta aparte sin filtro de estado. (Espejo de getCuotasActivosFuturas.)
  const compromisoIds = [...new Set(cuotas.map((c) => c.compromiso_id).filter((id) => id != null))];
  const { data: todasLasCuotas, error: errTotal } = await client
    .from("plan_pagos_compromiso")
    .select("compromiso_id")
    .in("compromiso_id", compromisoIds);
  if (errTotal) throw errTotal;
  const totalPorCompromiso = {};
  for (const row of todasLasCuotas || []) {
    totalPorCompromiso[row.compromiso_id] = (totalPorCompromiso[row.compromiso_id] || 0) + 1;
  }
  return cuotas.map((c) => ({ ...c, total_cuotas: totalPorCompromiso[c.compromiso_id] || 0 }));
};
